// IconButtonGroup.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, Input, message, Tooltip } from 'antd';
import {
  EditOutlined,
  CopyOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  MailOutlined,
  LoadingOutlined,
  ExpandOutlined,
  SyncOutlined,
  SolutionOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../auth/AuthContext';
import ConfirmationModal from './ConfirmationModal/ConfirmationModal';
import FullScreenModal from './FullScreenModal/FullScreenModal';
import axios from 'axios';
import { useDictation } from '../../../context/DictationContext';
import { diffWords } from 'diff';

const IconButtonGroup = ({
  feature,
  text,
  fileId,
  fetchEditedVersions,
  setSelectedVersion,
  setActivePanel,
  versionId,
  contentType
}) => {
  const auth = useAuth();
  const {
    isDictating,
    dictationStatus,
    interimTranscript,
    wordRectifications,
    setWordRectifications,
    registerComponent,
    setActiveComponent
  } = useDictation();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedText, setEditedText] = useState(text);
  const [loading, setLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [fullscreenModalOpen, setFullscreenModalOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
  
  // Store dictated sentences along with the position and a "processed" flag.
  const [dictatedTextSegments, setDictatedTextSegments] = useState([]);
  const textAreaRef = useRef(null);
  const componentId = useRef(
    `iconbuttongroup-${fileId || Math.random().toString(36).substr(2, 9)}`
  );

  // Update editedText when the prop text changes.
  useEffect(() => {
    setEditedText(text);
  }, [text]);

  // --------------------
  // Helper: Get rectified words by comparing the original (dictated) sentence with the current (edited) sentence.
  // This implementation is similar to your reference code.
  const getRectifiedWords = (baseText, newText) => {
    const diffs = diffWords(baseText, newText);
    const rectifiedWords = [];
    for (let i = 0; i < diffs.length; i++) {
      if (diffs[i].removed && i + 1 < diffs.length && diffs[i + 1].added) {
        const oldWord = diffs[i].value.trim();
        const newWord = diffs[i + 1].value.trim();
        if (oldWord && newWord) {
          rectifiedWords.push({
            replace: oldWord,
            with: newWord,
          });
        }
        i++;
      }
    }
    return rectifiedWords;
  };

  // --------------------
  // When dictated text is inserted, store the dictated sentence along with its position.
  useEffect(() => {
    if (editModalOpen) {
      const handleDictationTextInsert = (insertedText) => {
        const textAreaNode = textAreaRef.current?.resizableTextArea?.textArea;
        if (!textAreaNode) return;
        const currentPosition = {
          start: textAreaNode.selectionStart,
          end: textAreaNode.selectionEnd,
        };

        setEditedText((prevText) => {
          const before = prevText.substring(0, currentPosition.start);
          const after = prevText.substring(currentPosition.end);
          const newText = before + insertedText + after;
          // Save the dictated sentence with the current DOM-based position and mark as unprocessed.
          setTimeout(() => {
            setDictatedTextSegments((prevSegments) => {
              if (prevSegments.some((segment) => segment.text === insertedText)) {
                return prevSegments;
              }
              return [
                ...prevSegments,
                {
                  text: insertedText,
                  position: {
                    start: currentPosition.start,
                    // Initial end is set to start + insertedText.length; subsequent edits might change the length.
                    end: currentPosition.start + insertedText.length,
                  },
                  processed: false,
                }
              ];
            });
          }, 0);
          // Adjust caret position after insertion.
          setTimeout(() => {
            textAreaNode.focus();
            const newCaretPos = before.length + insertedText.length;
            textAreaNode.setSelectionRange(newCaretPos, newCaretPos);
          }, 0);
          return newText;
        });
      };

      registerComponent(componentId.current, handleDictationTextInsert);
      setActiveComponent(componentId.current);
    }
  }, [editModalOpen, registerComponent, setActiveComponent]);

  // --------------------
  // New approach: When the cursor moves, determine if the user has left the line where a dictated sentence was inserted.
  // If so, extract the edited sentence from the text area, compare it with the original dictated sentence,
  // and update the rectified words accordingly.
  const handleSelectionChange = (e) => {
    const { selectionStart, selectionEnd } = e.target;
    setCursorPosition({ start: selectionStart, end: selectionEnd });

    // Get the current full text from the textarea.
    const currentText = textAreaRef.current?.resizableTextArea?.textArea?.value || '';
    // Compute the current line number (by counting '\n' characters up to the current cursor).
    const currentLine = currentText.substring(0, selectionStart).split('\n').length;
    
    // Process each unprocessed dictated segment.
    setDictatedTextSegments((prevSegments) => {
      const updatedSegments = prevSegments.map((segment) => {
        if (!segment.processed) {
          // Determine the line number where the dictated sentence was inserted.
          const segmentLine = currentText.substring(0, segment.position.start).split('\n').length;
          if (currentLine !== segmentLine) {
            // User has moved to a different line (indicating finished editing for that sentence)
            // Determine the current boundary of the sentence (from insertion start to next newline).
            let endIndex = currentText.indexOf('\n', segment.position.start);
            if (endIndex === -1) {
              endIndex = currentText.length;
            }
            const editedSentence = currentText.substring(segment.position.start, endIndex);
            const rects = getRectifiedWords(segment.text, editedSentence);
            if (rects.length > 0) {
              setWordRectifications((prev) => [...prev, ...rects]);
            }
            // Mark the segment as processed.
            return { ...segment, processed: true };
          }
        }
        return segment;
      });
      return updatedSegments;
    });
  };

  // Also listen on mouse up to capture selection changes.
  const handleMouseUp = (e) => {
    handleSelectionChange(e);
  };

  const handleTextAreaFocus = () => {
    setActiveComponent(componentId.current);
  };

  // When text changes manually, simply update the state.
  const handleTextChange = (e) => {
    setEditedText(e.target.value);
  };

  // --------------------
  // Utility functions for copy, download, email, etc.
  const markdownToPlainText = (markdown) => {
    return markdown
      .replace(/[#*_>`~]/g, '')
      .replace(/\[(.*?)\]\(.*?\)/g, '$1')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/!\[(.*?)\]\(.*?\)/g, '$1')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
  };

  const stripHtmlTags = (html) => {
    const textWithLineBreaks = html.replace(/<br\s*\/?>/gi, '\n');
    const textOnly = textWithLineBreaks.replace(/<\/?[^>]+>/gi, '');
    return textOnly;
  };

  const copyToClipboard = async () => {
    try {
      let copiedText;
      if (contentType === 'transcript') {
        copiedText = stripHtmlTags(text);
      } else if (contentType === 'summary') {
        copiedText = markdownToPlainText(text);
      } else {
        copiedText = text;
      }
      await navigator.clipboard.writeText(copiedText);
      message.success('Copied!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const toCapitalCase = (str) => {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      const formData = new FormData();
      if (versionId) formData.set('version', versionId);
      formData.append('content_type', contentType);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/files/audio/${fileId}/download_summary`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${auth.user['access_token']}`,
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${toCapitalCase(contentType)}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (contentType === 'assessment_report') contentType = 'Assessment Report';
      message.success(`${toCapitalCase(contentType)} downloaded successfully!`);
    } catch (error) {
      console.error(`Error downloading ${toCapitalCase(contentType)}`, error);
      message.error(`Error downloading ${toCapitalCase(contentType)}`);
    }
    setDownloadLoading(false);
  };

  const handleSendEmail = async () => {
    setSendEmailLoading(true);
    try {
      const formData = new FormData();
      if (versionId) formData.set('version', versionId);
      formData.append('content_type', contentType);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/audio/${fileId}/send_summary_email`,
        {
          headers: {
            'Authorization': `Bearer ${auth.user['access_token']}`,
          },
          method: 'POST',
          body: formData,
        }
      );

      if (response.ok) {
        const result = await response.json();   
        message.success(result.message);
      } else {
        message.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email', error);
      message.error('Error sending email');
    }
    setSendEmailLoading(false);
  };

  // Add the helper function in your component.
  const finalizePendingDictations = () => {
    const textAreaNode = textAreaRef.current?.resizableTextArea?.textArea;
    if (!textAreaNode) return;
    
    const currentText = textAreaNode.value;
    
    setDictatedTextSegments((prevSegments) => {
      return prevSegments.map((segment) => {
        if (!segment.processed) {
          // Find the end of the current sentence (either at the next newline or at the end of the text).
          let endIndex = currentText.indexOf('\n', segment.position.start);
          if (endIndex === -1) {
            endIndex = currentText.length;
          }
          const editedSentence = currentText.substring(segment.position.start, endIndex);
          const rects = getRectifiedWords(segment.text, editedSentence);
          if (rects.length > 0) {
            // Append the new word rectifications.
            setWordRectifications((prev) => [...prev, ...rects]);
          }
          // Return a new segment with the processed flag set to true.
          return { ...segment, processed: true };
        }
        return segment;
      });
    });
  };


  const handleEdit = async () => {
    // Finalize any pending dictated segments before saving.
    finalizePendingDictations();

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('summary', editedText);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/audio/${fileId}/summary`,
        {
          headers: {
            'Authorization': `Bearer ${auth.user['access_token']}`,
          },
          method: 'PATCH',
          body: formData
        }
      );

      if (response.ok) {
        const result = await response.json();

        // Add new words to the user dictionary.
        if (wordRectifications && wordRectifications.length > 0) {
          for (const rectification of wordRectifications) {
            try {
              await axios.post(
                `${process.env.REACT_APP_API_URL}/dictionary/me`,
                {
                  replace: rectification.replace,
                  with_: rectification.with
                },
                {
                  headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${auth.user['access_token']}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
            } catch (dictError) {
              console.error('Error adding word to dictionary', dictError);
            }
          }
        }
        if (result?.version) {
          await fetchEditedVersions();
          setSelectedVersion(result.version);
          setActivePanel('02');
          setLoading(false);
          setEditModalOpen(false);
          setWordRectifications([])
          setDictatedTextSegments([])
        }
      } else {
        message.error('Failed to edit Summary');
      }
    } catch (error) {
      console.error('Error editing Summary', error);
      message.error('Error editing Summary');
    }
  };

  const handleEditRegenerate = async () => {
    // Finalize any pending dictated segments before saving.
    finalizePendingDictations();
    
    setRegenerateLoading(true);
    try {
      const formData = new FormData();
      formData.append('summary', editedText);
      if (versionId) formData.set('version_id', versionId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v2/files/audio/${fileId}/summary`,
        {
          headers: {
            'Authorization': `Bearer ${auth.user['access_token']}`,
          },
          method: 'PATCH',
          body: formData
        }
      );

      if (response.ok) {
        const result = await response.json();

        // Add new words to the user dictionary.
        if (wordRectifications && wordRectifications.length > 0) {
          for (const rectification of wordRectifications) {
            try {
              await axios.post(
                `${process.env.REACT_APP_API_URL}/dictionary/me`,
                {
                  replace: rectification.replace,
                  with_: rectification.with
                },
                {
                  headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${auth.user['access_token']}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
            } catch (dictError) {
              console.error('Error adding word to dictionary', dictError);
            }
          }
        }

        if (result?.version) {
          await fetchEditedVersions();
          setSelectedVersion(result.version);
          setActivePanel('02');
          setRegenerateLoading(false);
          setEditModalOpen(false);
          setWordRectifications([])
          setDictatedTextSegments([])
        }
      } else {
        message.error('Failed to edit Summary');
      }
    } catch (error) {
      console.error('Error editing Summary', error);
      message.error('Error editing Summary');
    }
  };

  const modalTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>Edit Text</span>
      <div style={{ marginRight: '20px' }}>
        <Button type="link" onClick={handleDownload} icon={<DownloadOutlined />} />
        <Button type="link" onClick={() => setFullscreenModalOpen(true)}>
          <ExpandOutlined />
        </Button>
      </div>
    </div>
  );

  const handleCloseModal = async () => {
    setEditModalOpen(false)
    setEditedText(text);
    setWordRectifications([])
    setDictatedTextSegments([])
  }
  
  console.log("dictatedTextSegments", dictatedTextSegments);
  console.log('wordRectifications', wordRectifications);

  return (
    <>
      <div>
        {feature.edit && (
          <Tooltip placement="top" title="Edit">
            <Button
              shape="circle"
              type="default"
              icon={<EditOutlined style={{ fontSize: '16px' }} />}
              style={{
                backgroundColor: 'transparent',
                color: '#4CAF50',
                borderColor: 'transparent',
                width: '30px',
                height: '30px'
              }}
              onClick={() => setEditModalOpen(true)}
            />
          </Tooltip>
        )}
        {feature.correct && (
          <Tooltip placement="top" title="Correct Text">
            <Button
              shape="circle"
              type="default"
              icon={<SolutionOutlined style={{ fontSize: '16px' }} />}
              style={{
                backgroundColor: 'transparent',
                color: '#FA8C16',
                borderColor: 'transparent',
                width: '30px',
                height: '30px'
              }}
              onClick={async () => {
                message.info("Transcript is processed automatically.");
              }}
            />
          </Tooltip>
        )}
        {feature.copy && (
          <Tooltip placement="top" title="Copy to Clipboard">
            <Button
              shape="circle"
              type="default"
              icon={<CopyOutlined style={{ fontSize: '16px' }} />}
              style={{
                backgroundColor: 'transparent',
                color: '#2196F3',
                borderColor: 'transparent',
                width: '30px',
                height: '30px'
              }}
              onClick={copyToClipboard}
            />
          </Tooltip>
        )}
        {feature.download && (
          <Tooltip placement="top" title="Download">
            <Button
              shape="circle"
              type="default"
              icon={
                downloadLoading ? (
                  <LoadingOutlined style={{ fontSize: '16px' }} />
                ) : (
                  <DownloadOutlined style={{ fontSize: '16px' }} />
                )
              }
              style={{
                backgroundColor: 'transparent',
                color: '#FF5722',
                borderColor: 'transparent',
                width: '30px',
                height: '30px'
              }}
              onClick={handleDownload}
            />
          </Tooltip>
        )}
        {feature.email && (
          <Tooltip placement="top" title="Send email">
            <Button
              shape="circle"
              type="default"
              icon={
                sendEmailLoading ? (
                  <LoadingOutlined style={{ fontSize: '16px' }} />
                ) : (
                  <MailOutlined style={{ fontSize: '16px' }} />
                )
              }
              style={{
                backgroundColor: 'transparent',
                color: '#7200e5',
                borderColor: 'transparent',
                width: '30px',
                height: '30px'
              }}
              onClick={() => setConfirmationModalOpen(true)}
            />
          </Tooltip>
        )}
        {feature.fullscreen && (
          <Tooltip placement="top" title="View in Fullscreen">
            <Button
              shape="circle"
              type="default"
              icon={<ExpandOutlined style={{ fontSize: '16px' }} />}
              style={{
                backgroundColor: 'transparent',
                color: '#876603',
                borderColor: 'transparent',
                width: '30px',
                height: '30px'
              }}
              onClick={() => setFullscreenModalOpen(true)}
            />
          </Tooltip>
        )}
      </div>
      <Modal
        title={modalTitle}
        open={editModalOpen}
        onOk={handleEdit}
        onCancel={() => handleCloseModal()}
        okText="Save"
        cancelText="Cancel"
        centered
        width={800}
        maskClosable={false}
        destroyOnClose
        keyboard={false}
        footer={[
          <Button key="cancel" onClick={() => handleCloseModal()}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={handleEdit}>
            Save
          </Button>,
          <Button
            key="submit2"
            loading={regenerateLoading}
            onClick={handleEditRegenerate}
            style={{ backgroundColor: '#ffa100', color: '#fff' }}
          >
            Save & Regenerate
          </Button>
        ]}
      >
        <Input.TextArea
          ref={textAreaRef}
          rows={15}
          value={editedText}
          onChange={handleTextChange}
          onFocus={handleTextAreaFocus}
          onSelect={handleSelectionChange}
          onKeyUp={handleSelectionChange}
          onClick={handleSelectionChange}
          onMouseUp={handleMouseUp}
        />
        {(dictationStatus || isDictating || interimTranscript) && (
          <div style={{ marginTop: 10 }}>
            {dictationStatus && (
              <div style={{ padding: 8, borderRadius: 4, background: '#f0f0f0' }}>
                <b>Status:</b> {dictationStatus}
              </div>
            )}
            {isDictating && (
              <div style={{ color: '#52c41a' }}>
                <SyncOutlined spin /> Listening... (Speak clearly into your microphone)
              </div>
            )}
            {interimTranscript && (
              <div style={{ padding: 8, borderRadius: 4, background: '#e6f7ff', color: '#1890ff' }}>
                <b>Preview:</b> {interimTranscript}
              </div>
            )}
          </div>
        )}
        <small style={{ color: 'red', fontWeight: 600, fontStyle: 'italic' }}>
          <InfoCircleOutlined style={{ marginRight: '10px' }} /><i><strong>Note:</strong> When starting dictation, wait for one second before speaking.</i>
        </small>
      </Modal>
      <ConfirmationModal
        open={confirmationModalOpen}
        setOpen={setConfirmationModalOpen}
        handleEmailSend={handleSendEmail}
        loading={sendEmailLoading}
      />
      <FullScreenModal
        open={fullscreenModalOpen}
        setOpen={setFullscreenModalOpen}
        text={text}
        contentType={contentType}
      />
    </>
  );
};

export default IconButtonGroup;
